import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { SITE } from "../lib/site";

const SettingsContext = createContext(null);

const CACHE_KEY = "pbg_site_settings";

const getInitialSettings = () => {
  const fallback = {
    site_name: SITE.name,
    company_email: SITE.email,
    company_phone: SITE.phone,
    company_address: SITE.address,
    banners: [],
    social_links: {
      facebook: "",
      linkedin: "",
      twitter: "",
      instagram: "",
      youtube: "",
      whatsapp: "",
      tiktok: "",
    },
  };

  if (typeof window === "undefined") return fallback;
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === "object") {
        return { ...fallback, ...parsed };
      }
    }
  } catch (_) {}
  return fallback;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(getInitialSettings);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then((r) => {
        if (r.data && r.data.success) {
          setSettings(r.data);
          setLoaded(true);
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(r.data));
          } catch (_) {}
        }
      })
      .catch((e) => {
        console.error("Failed to load settings from API:", e);
        setLoaded(true);
      });
  }, []);

  const phone = settings.company_phone || SITE.phone;
  const email = settings.company_email || SITE.email;
  const secondaryEmail = settings.company_secondary_email || SITE.secondaryEmail;
  const address = settings.company_address || SITE.address;
  const name = settings.site_name || SITE.name;
  
  // Format phone link
  const cleanPhone = phone.replace(/[^+\d]/g, "");
  const phoneLink = `tel:${cleanPhone}`;
  
  // Format WhatsApp link
  const whatsappUrl = settings.social_links?.whatsapp || `https://wa.me/${cleanPhone.replace("+", "")}?text=Hi%20Pickleballgearz%2C%20I%27d%20like%20a%20quote%20for%20pickleball%20products.`;
  
  const copyright = `© ${new Date().getFullYear()} ${name}`;

  const value = {
    name,
    shortName: SITE.shortName,
    phone,
    phoneLink,
    whatsappLink: whatsappUrl,
    email,
    secondaryEmail,
    address,
    copyright,
    siteLogo: settings.site_logo || null,
    siteFavicon: settings.site_favicon || null,
    footerAbout: settings.footer_about || "Premium pickleball manufacturing for brands, clubs, and distributors. OEM, ODM, and private label programs — from first sample to final container.",
    enablePromoNotice: Boolean(settings.enable_promo_notice),
    promoNoticeText: settings.promo_notice_text || "Global Export Dispatch — USAPA Compliant Equipment",
    promoNoticeUrl: settings.promo_notice_url || "/moq",
    navigationMenu: settings.navigation_menu || null,
    navigationSettings: settings.navigation_settings || {},
    banners: settings.banners || [],
    counters: settings.counters || [],
    heroBadges: settings.hero_badges || [],
    aboutPage: settings.about_page || {},
    factory: settings.factory || {},
    social_links: {
      facebook: settings.social_links?.facebook || "https://www.facebook.com/profile.php?id=61594506620846",
      instagram: settings.social_links?.instagram || "https://www.instagram.com/pickleballgearz/",
      linkedin: settings.social_links?.linkedin || "",
      twitter: settings.social_links?.twitter || "",
      youtube: settings.social_links?.youtube || "",
      whatsapp: settings.social_links?.whatsapp || "",
      tiktok: settings.social_links?.tiktok || "",
    },
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
